import { HashLocationStrategy, LocationStrategy, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEnGb from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localePt from '@angular/common/locales/pt';
import {
    ApplicationConfig,
    DEFAULT_CURRENCY_CODE,
    importProvidersFrom,
    LOCALE_ID,
    provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { QuillModule } from 'ngx-quill';
import { coreRoutes } from './app/modules/_core/core.routes';
import {
    DEFAULT_LANGUAGE,
    FALLBACK_LANGUAGE,
    resolveBootstrapLanguage,
} from './app/shared/i18n/i18n.constants';
import { AccessTokenInterceptor } from './app/shared/interceptors/access-token.interceptor';
import { AiEditMockInterceptor } from './app/modules/document/pages/edit/services/ai-edit-mock/ai-edit-mock.interceptor';
import { provideRaUiIntl } from './app/shared/ra-ui-intl.provider';
import { I18N_VERSION } from './i18n-version';

registerLocaleData(localePt, 'pt-BR');
registerLocaleData(localeEnGb, 'en-GB');
registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeDe, 'de-DE');
registerLocaleData(localeFr, 'fr-FR');

export const appConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        provideHttpClient(withInterceptorsFromDi()),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                // O `?v=` é cache-busting: sem ele a URL do dicionário é fixa (os bundles do
                // Angular têm hash, este arquivo não) e o edge, que serve estático com
                // `s-maxage=86400`, entrega o dicionário antigo por até 24h depois do deploy —
                // a tela nova aparece com as chaves cruas. `I18N_VERSION` é o hash do conteúdo
                // mesclado, gerado por scripts/merge-i18n.mjs a cada build.
                suffix: `.json?v=${I18N_VERSION}`,
            }),
            fallbackLang: FALLBACK_LANGUAGE,
            lang: DEFAULT_LANGUAGE,
        }),
        provideEnvironmentNgxMask(),
        provideRaUiIntl(),
        provideRouter(coreRoutes),
        importProvidersFrom(QuillModule.forRoot()),
        // Antes era `useValue: DEFAULT_LANGUAGE` (pt-BR fixo): todo DatePipe/CurrencyPipe nativo
        // formatava em português, mesmo para o usuário alemão ou britânico. Agora sai do idioma
        // já persistido (cookie do usuário → localStorage → navegador).
        { provide: LOCALE_ID, useFactory: resolveBootstrapLanguage },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'BRL' },
        { provide: LocationStrategy, useClass: HashLocationStrategy },
        { provide: HTTP_INTERCEPTORS, useClass: AccessTokenInterceptor, multi: true },
        // Mock da API do modo "Editar com IA" (só age com environment.AI_EDIT_MOCK ligado).
        { provide: HTTP_INTERCEPTORS, useClass: AiEditMockInterceptor, multi: true },
    ],
};
